import type { Look } from "../requests/labels";
import type { IconName } from "../ui/icon";

// The words of the staff itinerary screens. Every state has a word AND a glyph (an icon): nothing is shown by colour alone. The issue words
// are plain statements of what is wrong, never provider text.

export type KindLabel = Readonly<{ word: string; icon: IconName }>;

const KIND_LABELS: Readonly<Record<string, KindLabel>> = {
  CATALOG_ITEM: { word: "Catalog item", icon: "gem" },
  EXTERNAL_PLACE: { word: "Place", icon: "map-pin" },
  TRANSIT: { word: "Travel", icon: "airplane-02" },
  BUFFER: { word: "Buffer", icon: "timer-01" },
  CUSTOM_ACTIVITY: { word: "Custom activity", icon: "file-edit" },
  CONCIERGE_REQUEST: { word: "Concierge request", icon: "mail-01" },
};

export const kindLabel = (kind: string): KindLabel =>
  KIND_LABELS[kind] ?? { word: kind, icon: "circle" };

export const ITEM_STATUS_LABELS: Readonly<Record<string, string>> = {
  PLANNED: "Planned",
  TENTATIVE: "Tentative",
  CANCELLED: "Cancelled",
};

export const SENSITIVITY_LABELS: Readonly<Record<string, string>> = {
  NONE: "Not weather sensitive",
  OUTDOOR: "Outdoor: weather sensitive",
  WATER: "On the water: weather sensitive",
  HEIGHT: "At height: weather sensitive",
};

/** How a plan status looks. The word comes from `stateWord("itinerary", "staffShort", status)`. */
export const PLAN_STATUS_LOOK: Readonly<Record<string, Look>> = {
  DRAFT: { tone: "neutral", icon: "edit-02" },
  ACTIVE: { tone: "success", icon: "star" },
  SUPERSEDED: { tone: "neutral", icon: "time-quarter" },
  ARCHIVED: { tone: "neutral", icon: "archive-01" },
};

export const FEASIBILITY_LABELS: Readonly<Record<string, string>> = {
  UNKNOWN: "Not judged: travel times are unknown",
  FEASIBLE: "Feasible: every checked gap holds the travel time",
  INFEASIBLE: "Infeasible: at least one gap is too short",
};

export const TIME_ZONE_SOURCE_LABELS: Readonly<Record<string, string>> = {
  CATALOG: "zone from the catalog item",
  STAFF: "zone confirmed by staff",
  CUSTOMER: "zone confirmed by the customer",
};

export type IssueWords = Readonly<{ title: string; detail: string }>;

const ISSUES: Readonly<Record<string, IssueWords>> = {
  ITINERARY_EMPTY: { title: "The plan is empty", detail: "It has no items yet." },
  SECTION_POSITION_DUPLICATE: {
    title: "Two days share a position",
    detail: "The order of the days is not clear. Reload the plan.",
  },
  ITEM_POSITION_DUPLICATE: {
    title: "Two items share a position",
    detail: "The order of the items is not clear. Reload the plan.",
  },
  DAY_DATE_DUPLICATE: {
    title: "Two days have the same date",
    detail: "Each date should appear once. Move the items into one day.",
  },
  DAY_MISSING_TIME_ZONE: {
    title: "A day has no time zone",
    detail: "A dated day needs the zone its clock times are read in.",
  },
  DAY_OUTSIDE_TRIP_DATES: {
    title: "A day is outside the trip dates",
    detail: "Check the trip dates or move the day.",
  },
  ITEM_INTERVAL_INVALID: {
    title: "An item ends before it starts",
    detail: "Fix its end time or its length.",
  },
  ITEM_MISSING_TIME_ZONE: {
    title: "A timed item has no time zone",
    detail: "Choose the zone its clock time is read in.",
  },
  ITEM_TIME_ZONE_UNKNOWN: {
    title: "An item has a time zone the system does not know",
    detail: "Choose a zone from the list.",
  },
  ITEM_START_NOT_ON_DAY: {
    title: "An item starts on another day than its day",
    detail:
      "Its start, read in its own zone, falls on a different date. Move it to that day or change its time.",
  },
  ITEM_SCHEDULED_IN_FLEXIBLE_SECTION: {
    title: "A timed item sits in the undated group",
    detail: "Move it into a day, or make it untimed.",
  },
  ITEM_ALL_DAY_IN_FLEXIBLE_SECTION: {
    title: "An all-day item sits in the undated group",
    detail: "Move it into a day.",
  },
  ITEMS_OVERLAP: {
    title: "Two items overlap in time",
    detail: "One starts before the other ends. Move one of them.",
  },
  ITEM_LOCATION_MISSING: {
    title: "An item has no place",
    detail: "Route checks need a place for each timed item.",
  },
  TIME_NOT_EXISTENT: {
    title: "A clock time does not exist",
    detail: "The clocks jump over it. Choose the first valid time.",
  },
  TIME_AMBIGUOUS: {
    title: "A clock time happens twice",
    detail: "The clocks go back. Choose the earlier or the later time.",
  },
  ITEM_LOCAL_TIME_DRIFT: {
    title: "The time rules of a zone changed",
    detail:
      "The clock time that was entered now reads as a different instant. The stored instant is unchanged. Correct the time with an edit.",
  },
  LOCATION_UNRESOLVED: {
    title: "A place could not be found",
    detail: "Route and weather checks cannot use it. Add a place the system can locate.",
  },
  LOCATION_AMBIGUOUS: {
    title: "A place matches more than one location",
    detail: "Ask the customer which place they mean.",
  },
  ITEM_ZONE_LOCATION_MISMATCH: {
    title: "An item's time zone does not match its place",
    detail: "Check that the zone is right for where the item happens.",
  },
  TRAVEL_TIME_EXCEEDS_GAP: {
    title: "The travel time is longer than the gap",
    detail:
      "There is not enough time to get from one item to the next. Move an item. Staff can acknowledge a gap that a route check found, with a reason. Nobody can acknowledge the fixed minimum between two cities.",
  },
  TRAVEL_TIGHT: {
    title: "Travel is tight",
    detail: "The gap holds the travel time with little spare time.",
  },
  ROUTE_UNVERIFIED: {
    title: "A travel time could not be checked",
    detail: "No route answer is available. The plan is unchanged and nothing was invented.",
  },
  WEATHER_RISK_HIGH: {
    title: "High weather risk",
    detail: "The forecast is poor for a weather-sensitive item. The item is not cancelled.",
  },
  WEATHER_RISK_SEVERE: {
    title: "Severe weather risk",
    detail: "Review this item with the customer. The forecast alone does not cancel anything.",
  },
  WEATHER_UNVERIFIED: {
    title: "Weather could not be checked",
    detail: "No forecast is available for an item. The plan is unchanged.",
  },
  LINKED_ITEM_CHANGED: {
    title: "A change touches an item with a request",
    detail: "Check the linked request before you activate.",
  },
};

export const issueWords = (code: string): IssueWords =>
  ISSUES[code] ?? { title: code, detail: "No plain-words explanation is available for this code." };

export const SEVERITY_LABELS = {
  BLOCKING: { word: "Blocking", tone: "danger", icon: "alert-diamond" },
  WARNING: { word: "Warning", tone: "warning", icon: "alert-02" },
} as const satisfies Record<string, { word: string } & Look>;

export const EVIDENCE_STATE_LABELS = {
  VERIFIED: { word: "Verified", tone: "success", icon: "checkmark-circle-02" },
  UNVERIFIED: { word: "Unverified", tone: "warning", icon: "help-circle" },
  STALE: { word: "Stale", tone: "warning", icon: "hourglass" },
} as const satisfies Record<string, { word: string } & Look>;
